<template>
  <div class="um">
    <header class="um__header">
      <div>
        <h1 class="um__title">User Management</h1>
        <p class="um__subtitle">Manage your team and control what needs each person's attention.</p>
      </div>
      <span v-if="users.length" class="um__count"
        >{{ users.length }} {{ users.length === 1 ? "person" : "people" }}</span
      >
    </header>

    <section class="um__invite">
      <form class="um__invite-form" @submit.prevent="sendInvite">
        <div class="um__field">
          <v-icon icon="mdi-email-plus-outline" size="20" class="um__field-icon" />
          <input
            v-model="inviteEmail"
            type="email"
            class="um__input"
            placeholder="Invite a teammate by email"
            :disabled="seatsFull" />
        </div>
        <button type="submit" class="um__btn um__btn--primary" :disabled="inviting || seatsFull || !inviteEmail">
          <v-progress-circular v-if="inviting" indeterminate size="16" width="2" />
          <span v-else>Send invite</span>
        </button>
      </form>
      <p v-if="seatsFull" class="um__hint">All seats are in use — revoke a pending invite or contact Lamar Health.</p>
    </section>

    <transition name="um-fade">
      <div v-if="successMessage" class="um__toast um__toast--success">{{ successMessage }}</div>
    </transition>
    <div v-if="errors.length" class="um__toast um__toast--error">
      <div v-for="error in errors" :key="error">{{ error }}</div>
    </div>

    <section class="um__table">
      <div class="um__table-head">
        <span>User</span>
        <span>Role</span>
        <span>Status</span>
        <span />
        <span />
      </div>

      <div v-if="loading" class="um__empty">
        <v-progress-circular indeterminate color="primary" size="28" width="3" />
      </div>
      <div v-else-if="!users.length" class="um__empty">No users yet — send your first invite above.</div>

      <div v-for="user in users" v-else :key="user.id" class="um__row">
        <div class="um__col--user">
          <span class="um__avatar" :style="{ background: avatarColor(user) }">{{ initials(user) }}</span>
          <span class="um__user-meta">
            <span class="um__user-name">{{ fullName(user) }}</span>
            <span class="um__user-email">{{ user.email }}</span>
          </span>
        </div>
        <div class="um__role">
          <span class="um__chip" :class="user.isAdmin ? 'um__chip--admin' : 'um__chip--member'">
            {{ user.isAdmin ? "Admin" : "Member" }}
          </span>
          <v-tooltip v-if="user.assignedCasesOnly" location="top" text="Sees only cases assigned to them">
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="um__restricted-badge"
                role="img"
                tabindex="0"
                aria-label="Sees only cases assigned to them">
                <v-icon icon="mdi-lock" size="11" />
              </span>
            </template>
          </v-tooltip>
          <v-tooltip v-if="hasInsights(user) && !user.isAdmin" location="top" text="Has insights access">
            <template #activator="{ props: tipProps }">
              <span
                v-bind="tipProps"
                class="um__restricted-badge"
                role="img"
                tabindex="0"
                aria-label="Has insights access">
                <v-icon icon="mdi-chart-box-outline" size="11" />
              </span>
            </template>
          </v-tooltip>
        </div>
        <div>
          <span class="um__chip" :class="user.status === 'active' ? 'um__chip--active' : 'um__chip--pending'">
            <span class="um__dot" />
            {{ user.status === "active" ? "Active" : "Pending" }}
          </span>
        </div>
        <div class="um__spacer" />
        <div class="um__col--actions">
          <v-menu v-if="hasActions(user)" location="bottom end">
            <template #activator="{ props: menuProps }">
              <button type="button" class="um__btn um__btn--ghost" v-bind="menuProps">
                Actions
                <v-icon icon="mdi-chevron-down" size="16" />
              </button>
            </template>
            <v-list density="compact" class="um__actions-menu">
              <v-list-item
                v-if="canConfigure(user)"
                prepend-icon="mdi-cog-outline"
                title="Action Needed"
                @click="openConfig(user)" />
              <v-list-item
                v-if="canRestrict(user)"
                :prepend-icon="user.assignedCasesOnly ? 'mdi-lock-open-variant-outline' : 'mdi-lock-outline'"
                :title="user.assignedCasesOnly ? 'Show all cases' : 'Only assigned cases'"
                @click="toggleAssignedOnly(user)" />
              <v-list-item
                v-if="!user.isAdmin && !user.assignedCasesOnly"
                :prepend-icon="hasInsights(user) ? 'mdi-chart-box' : 'mdi-chart-box-outline'"
                :title="hasInsights(user) ? 'Revoke Denial Insights' : 'Grant Denial Insights'"
                @click="toggleInsights(user)" />
              <v-list-item
                v-if="!user.isAdmin && !user.assignedCasesOnly"
                :prepend-icon="hasProviderInsights(user) ? 'mdi-account-heart' : 'mdi-account-heart-outline'"
                :title="hasProviderInsights(user) ? 'Revoke Providers Management' : 'Grant Providers Management'"
                @click="toggleProviderInsights(user)" />
              <v-list-item
                v-if="canGrantCompanies(user)"
                prepend-icon="mdi-domain"
                title="Companies"
                @click="openCompanies(user)" />
              <v-list-item
                v-if="user.status === 'pending'"
                prepend-icon="mdi-email-remove-outline"
                title="Revoke invite"
                class="um__actions-menu__danger"
                @click="promptRevoke(user)" />
              <v-list-item
                v-if="canDelete(user)"
                prepend-icon="mdi-account-remove-outline"
                title="Remove"
                class="um__actions-menu__danger"
                @click="promptDelete(user)" />
            </v-list>
          </v-menu>
        </div>
      </div>
    </section>

    <!-- Revoke pending invite -->
    <v-dialog v-model="confirmRevokeOpen" max-width="420">
      <div class="um__dialog">
        <h2 class="um__dialog-title">Revoke invite</h2>
        <p class="um__dialog-body">
          Revoke the pending invite for <strong>{{ revokeTarget?.email }}</strong
          >?
        </p>
        <div class="um__dialog-actions">
          <button type="button" class="um__btn um__btn--ghost" @click="confirmRevokeOpen = false">Cancel</button>
          <button type="button" class="um__btn um__btn--danger" :disabled="revoking" @click="confirmRevoke">
            <v-progress-circular v-if="revoking" indeterminate size="16" width="2" />
            <span v-else>Revoke</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Per-user Action Needed config -->
    <v-dialog v-model="configOpen" max-width="460">
      <div class="um__dialog um__config">
        <div class="um__config-head">
          <div class="um__config-head-top">
            <h2 class="um__dialog-title">Action Needed</h2>
            <div class="um__segment">
              <button
                v-for="s in SCOPES"
                :key="s.key"
                type="button"
                class="um__segment-btn"
                :class="{ 'um__segment-btn--active': configScope === s.key }"
                @click="configScope = s.key">
                {{ s.label }}
              </button>
            </div>
          </div>
          <p class="um__config-sub">
            Which statuses require <strong>{{ configTarget && fullName(configTarget) }}</strong
            >'s attention
          </p>
        </div>

        <div class="um__config-body">
          <div v-if="configLoading" class="um__empty">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <div v-else class="um__an-list">
            <label
              v-for="opt in currentOptions"
              :key="opt.key"
              class="um__an-row"
              :class="{ 'um__an-row--on': isSelected(opt) }">
              <span class="um__an-label">{{ statusLabel(opt.key) }}</span>
              <v-switch
                :model-value="isSelected(opt)"
                density="compact"
                hide-details
                color="primary"
                class="um__an-switch"
                @update:model-value="setToggle(opt, $event)" />
            </label>
          </div>
          <p v-if="!configLoading" class="um__config-count">
            {{ selectedCount }} of {{ currentOptions.length }} selected
          </p>
          <label v-if="!configLoading && canLock" class="um__lock-row">
            <div class="um__lock-text">
              <span class="um__lock-title">Lock settings</span>
              <span class="um__lock-sub">Prevent this user from editing their own Action Needed settings</span>
            </div>
            <v-switch v-model="configLocked" density="compact" hide-details color="primary" class="um__an-switch" />
          </label>
        </div>

        <div class="um__dialog-actions">
          <button type="button" class="um__btn um__btn--ghost" :disabled="configSaving" @click="configOpen = false">
            Cancel
          </button>
          <button
            type="button"
            class="um__btn um__btn--primary"
            :disabled="configSaving || configLoading"
            @click="saveConfig">
            <v-progress-circular v-if="configSaving" indeterminate size="16" width="2" />
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <v-dialog v-model="companiesOpen" max-width="460">
      <div class="um__dialog">
        <h2 class="um__dialog-title">Companies this user can act as</h2>
        <p class="um__config-sub">
          Let <strong>{{ companiesTarget && fullName(companiesTarget) }}</strong> switch to and act on behalf of these
          companies.
        </p>
        <div class="um__config-body">
          <div v-if="companiesLoading" class="um__empty">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
          <v-select
            v-else
            v-model="companyGrants"
            :items="grantablePool"
            item-title="name"
            item-value="id"
            multiple
            chips
            closable-chips
            density="comfortable"
            variant="outlined"
            hide-details
            label="Companies" />
        </div>
        <div class="um__dialog-actions">
          <button
            type="button"
            class="um__btn um__btn--ghost"
            :disabled="companiesSaving"
            @click="companiesOpen = false">
            Cancel
          </button>
          <button
            type="button"
            class="um__btn um__btn--primary"
            :disabled="companiesSaving || companiesLoading"
            @click="saveCompanies">
            <v-progress-circular v-if="companiesSaving" indeterminate size="16" width="2" />
            <span v-else>Save</span>
          </button>
        </div>
      </div>
    </v-dialog>

    <ConfirmDialog
      v-model="confirmDeleteOpen"
      title="Remove user"
      :message="deleteMessage"
      confirm-text="Remove"
      variant="danger"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="confirmDeleteOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore, useActionNeededStore } from "@/stores";
import usersService from "@/services/usersService";
import { insightsService } from "@/services/insightsService";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog.vue";
import { tryParseApiErrors } from "@/utils";
import { SCOPE, toBackendScope } from "@/constants/scope";
import { groupActionNeededOptions } from "@/constants/actionNeeded";
import { PA_STATUS_LABEL_BY_KEY } from "@/models/paStatus";
import { INTAKE_STATUS_LABEL_BY_KEY } from "@/models/intakeStatus";

const router = useRouter();
const authStore = useAuthStore();
const actionNeededStore = useActionNeededStore();

const users = ref([]);
const insightsAccess = ref({});
const providerInsightsAccess = ref({});
const seatsUsed = ref(0);
const seatsTotal = ref(0);
const loading = ref(false);
const inviteEmail = ref("");
const inviting = ref(false);
const errors = ref([]);
const successMessage = ref("");

const confirmRevokeOpen = ref(false);
const revokeTarget = ref(null);
const revoking = ref(false);

const SCOPES = [
  { key: SCOPE.PA, label: "Prior Auth" },
  { key: SCOPE.INTAKE, label: "Intake" },
];

const confirmDeleteOpen = ref(false);
const deleteTarget = ref(null);
const deleting = ref(false);

const canDelete = (user) =>
  user.status === "active" && !user.isAdmin && String(user.id) !== String(authStore.userInfo.pk);

const deleteMessage = computed(() =>
  deleteTarget.value
    ? `Remove ${deleteTarget.value.email}? They'll be deactivated and can no longer sign in — their records are kept.`
    : ""
);
const AVATAR_COLORS = ["#2470CA", "#3D9E4C", "#7A5AF8", "#F08523", "#0EA5A4", "#E83D52"];

const configOpen = ref(false);
const configTarget = ref(null);
const configScope = ref(SCOPE.PA);
const configLoading = ref(false);
const configSaving = ref(false);
const options = ref({ [SCOPE.PA]: [], [SCOPE.INTAKE]: [] });
const selected = ref({ [SCOPE.PA]: [], [SCOPE.INTAKE]: [] });
// Snapshot of what each scope showed when the dialog opened (may be an inherited
// company default), so save only writes scopes the admin actually changed.
const loaded = ref({ [SCOPE.PA]: [], [SCOPE.INTAKE]: [] });
const configLocked = ref(false);
const loadedLocked = ref(false);
// Locking yourself is pointless (admins always edit here) — only members can be locked.
const canLock = computed(() => configTarget.value && !configTarget.value.isAdmin);

const grantablePool = ref([]);
const companiesOpen = ref(false);
const companiesTarget = ref(null);
const companyGrants = ref([]);
const companiesLoading = ref(false);
const companiesSaving = ref(false);

const seatsFull = computed(() => seatsTotal.value > 0 && seatsUsed.value >= seatsTotal.value);
const currentOptions = computed(() => groupActionNeededOptions(configScope.value, options.value[configScope.value]));
const currentSelected = computed(() => selected.value[configScope.value] || []);
const selectedCount = computed(() => currentOptions.value.filter(isSelected).length);

const fullName = (user) => `${user.firstName} ${user.lastName}`.trim() || "—";
const initials = (user) => {
  const parts = `${user.firstName || ""} ${user.lastName || ""}`.trim().split(/\s+/).filter(Boolean);
  if (parts.length) {
    return parts
      .slice(0, 2)
      .map((p) => p[0].toUpperCase())
      .join("");
  }
  return (user.email || "?")[0].toUpperCase();
};
const avatarColor = (user) => {
  const seed = (user.email || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[seed % AVATAR_COLORS.length];
};
const statusLabel = (key) =>
  (configScope.value === SCOPE.PA ? PA_STATUS_LABEL_BY_KEY[key] : INTAKE_STATUS_LABEL_BY_KEY[key]) || key;

// Admins configure Action Needed for normal (non-admin) users and for themselves.
const isSelf = (user) => String(user.id) === String(authStore.userInfo.pk);
const canConfigure = (user) => user.status === "active" && (!user.isAdmin || isSelf(user));
const isSelected = (opt) => opt.keys.some((key) => currentSelected.value.includes(key));

function setToggle(opt, val) {
  const list = selected.value[configScope.value];
  for (const key of opt.keys) {
    const idx = list.indexOf(key);
    if (val && idx === -1) list.push(key);
    else if (!val && idx !== -1) list.splice(idx, 1);
  }
}

const loadUsers = async () => {
  loading.value = true;
  try {
    const { results, seatsUsed: used, seatsTotal: total } = await usersService.listUsers();
    users.value = results;
    seatsUsed.value = used;
    seatsTotal.value = total;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    loading.value = false;
  }
};

const sendInvite = async () => {
  inviting.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    const email = inviteEmail.value;
    const result = await usersService.inviteUser(email);
    successMessage.value =
      result?.status === "reactivated"
        ? `${email} reactivated — their access has been restored.`
        : `Invite sent to ${email}.`;
    inviteEmail.value = "";
    await loadUsers();
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    inviting.value = false;
  }
};

const promptRevoke = (user) => {
  revokeTarget.value = user;
  confirmRevokeOpen.value = true;
};

const confirmRevoke = async () => {
  revoking.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    await usersService.revokeInvite(revokeTarget.value.id);
    successMessage.value = `Invite for ${revokeTarget.value.email} revoked.`;
    confirmRevokeOpen.value = false;
    await loadUsers();
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    revoking.value = false;
  }
};

const sameStatuses = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const openConfig = async (user) => {
  configTarget.value = user;
  configScope.value = SCOPE.PA;
  configOpen.value = true;
  configLoading.value = true;
  errors.value = [];
  try {
    const data = await usersService.getUserActionNeeded(user.id);
    options.value = { [SCOPE.PA]: data.priorAuth.options, [SCOPE.INTAKE]: data.intake.options };
    selected.value = { [SCOPE.PA]: [...data.priorAuth.statuses], [SCOPE.INTAKE]: [...data.intake.statuses] };
    loaded.value = { [SCOPE.PA]: [...data.priorAuth.statuses], [SCOPE.INTAKE]: [...data.intake.statuses] };
    configLocked.value = data.locked === true;
    loadedLocked.value = configLocked.value;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    configOpen.value = false;
  } finally {
    configLoading.value = false;
  }
};

const saveConfig = async () => {
  configSaving.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    // Only write scopes the admin actually changed — PUTting an untouched scope
    // would freeze its inherited company default into an explicit override.
    for (const scope of [SCOPE.PA, SCOPE.INTAKE]) {
      if (sameStatuses(selected.value[scope], loaded.value[scope])) continue;
      await usersService.updateUserActionNeeded(configTarget.value.id, toBackendScope(scope), selected.value[scope]);
    }
    if (canLock.value && configLocked.value !== loadedLocked.value) {
      await usersService.updateUserActionNeededLock(configTarget.value.id, configLocked.value);
    }
    // Editing your own statuses changes what the dashboards resolve for you —
    // refresh the cached store so the change shows without a hard reload.
    if (isSelf(configTarget.value)) await actionNeededStore.reload();
    successMessage.value = `Action Needed updated for ${fullName(configTarget.value)}.`;
    configOpen.value = false;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    configSaving.value = false;
  }
};

const canGrantCompanies = (user) => user.status === "active" && grantablePool.value.length > 0;

const hasActions = (user) =>
  canConfigure(user) || canRestrict(user) || canGrantCompanies(user) || user.status === "pending" || canDelete(user);

// Restricting an admin is rejected server-side too — admins always see all cases.
const canRestrict = (user) => !user.isAdmin;

const toggleAssignedOnly = async (user) => {
  errors.value = [];
  successMessage.value = "";
  const next = !user.assignedCasesOnly;
  try {
    await usersService.updateUserAssignedOnly(user.id, next);
    user.assignedCasesOnly = next;
    successMessage.value = next
      ? `${fullName(user)} now sees only their assigned cases.`
      : `${fullName(user)} can see all cases again.`;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  }
};

const hasInsights = (user) => Boolean(insightsAccess.value[user.id]);

const hasProviderInsights = (user) => Boolean(providerInsightsAccess.value[user.id]);

const toggleInsights = async (user) => {
  errors.value = [];
  successMessage.value = "";
  const next = !hasInsights(user);
  try {
    await insightsService.setAccess(user.id, next);
    insightsAccess.value = { ...insightsAccess.value, [user.id]: next };
    successMessage.value = next
      ? `${fullName(user)} can now view Denial Insights.`
      : `${fullName(user)} no longer has Denial Insights access.`;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  }
};

const toggleProviderInsights = async (user) => {
  errors.value = [];
  successMessage.value = "";
  const next = !hasProviderInsights(user);
  try {
    await insightsService.setAccess(user.id, next, "view_provider_insights");
    providerInsightsAccess.value = { ...providerInsightsAccess.value, [user.id]: next };
    successMessage.value = next
      ? `${fullName(user)} can now view Providers Management.`
      : `${fullName(user)} no longer has Providers Management access.`;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  }
};

const loadInsightsAccess = async () => {
  try {
    const rows = await insightsService.getAccessList();
    insightsAccess.value = Object.fromEntries(rows.map((row) => [row.id, row.hasAccess]));
    providerInsightsAccess.value = Object.fromEntries(rows.map((row) => [row.id, row.hasProviderAccess]));
  } catch {
    insightsAccess.value = {};
    providerInsightsAccess.value = {};
  }
};

const loadGrantablePool = async () => {
  try {
    const { pool } = await usersService.getGrantableCompanies();
    grantablePool.value = pool || [];
  } catch {
    grantablePool.value = [];
  }
};

const openCompanies = async (user) => {
  companiesTarget.value = user;
  companiesOpen.value = true;
  companiesLoading.value = true;
  errors.value = [];
  try {
    const { grantedIds } = await usersService.getUserCompanies(user.id);
    companyGrants.value = [...(grantedIds || [])];
  } catch (error) {
    errors.value = tryParseApiErrors(error);
    companiesOpen.value = false;
  } finally {
    companiesLoading.value = false;
  }
};

const saveCompanies = async () => {
  companiesSaving.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    await usersService.updateUserCompanies(companiesTarget.value.id, companyGrants.value);
    successMessage.value = `Companies updated for ${fullName(companiesTarget.value)}.`;
    companiesOpen.value = false;
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    companiesSaving.value = false;
  }
};

const promptDelete = (user) => {
  deleteTarget.value = user;
  confirmDeleteOpen.value = true;
};

const confirmDelete = async () => {
  deleting.value = true;
  errors.value = [];
  successMessage.value = "";
  try {
    await usersService.deleteUser(deleteTarget.value.id);
    successMessage.value = `${deleteTarget.value.email} removed.`;
    confirmDeleteOpen.value = false;
    await loadUsers();
  } catch (error) {
    errors.value = tryParseApiErrors(error);
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  if (!authStore.userInfo.isAdmin) {
    router.replace({ name: "home" });
    return;
  }
  loadUsers();
  loadInsightsAccess();
  loadGrantablePool();
});
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

$um-radius: 14px;
$um-border: 1px solid rgba(#042143, 0.08);
$um-grid: 320px 104px 128px minmax(16px, 1fr) 120px;

.um {
  max-width: 1060px;
  margin: 0 auto;
  padding: 40px 40px 56px;
  font-family: $lh_font_family_dm_sans;
  color: $lh_color_navy_dark;
}

.um__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
}

.um__title {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.um__subtitle {
  margin-top: 6px;
  color: $lh_color_blue_gray;
  font-size: 13px;
}

.um__count {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: $lh_color_blue_gray;
  background: white;
  border: $um-border;
  border-radius: 999px;
  padding: 6px 14px;
  white-space: nowrap;
}

.um__invite {
  margin-bottom: 20px;
}

.um__invite-form {
  display: flex;
  gap: 12px;
}

.um__field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  background: $lh_field_bg;
  border: 1px solid $lh_field_border;
  border-radius: $lh_field_radius;
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover:not(:focus-within) {
    border-color: $lh_field_border_hover;
  }

  &:focus-within {
    border-color: $lh_color_blue;
    box-shadow: $lh_field_focus_ring;
  }
}

.um__field-icon {
  color: $lh_color_blue_gray;
}

.um__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  padding: 10px 0;
  font-family: $lh_font_family_dm_sans;
  font-size: 14px;
  color: $lh_color_navy_dark;

  &::placeholder {
    color: $lh_color_blue_gray;
  }
}

.um__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: none;
  border-radius: 10px;
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.08s, background 0.15s, box-shadow 0.15s, color 0.15s;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:active:not(:disabled) {
    transform: translateY(1px);
  }
}

.um__btn--primary {
  background: $lh_color_blue;
  color: white;
  box-shadow: 0 2px 8px rgba(#2470ca, 0.25);
  &:hover:not(:disabled) {
    background: darken($lh_color_blue, 6%);
  }
}

.um__btn--ghost {
  background: transparent;
  color: $lh_color_blue_gray;
  border: $um-border;
  &:hover:not(:disabled) {
    color: $lh_color_navy_dark;
    background: $lh_color_snowy_white;
  }
}

.um__btn--danger {
  background: $lh_color_red;
  color: white;
  &:hover:not(:disabled) {
    background: darken($lh_color_red, 6%);
  }
}

.um__hint,
.um__config-sub {
  color: $lh_color_blue_gray;
  font-size: 13px;
  margin-top: 8px;
}

.um__toast {
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 13px;
  margin-bottom: 16px;
}
.um__toast--success {
  background: rgba(#3d9e4c, 0.1);
  color: darken($lh_color_green, 8%);
}
.um__toast--error {
  background: rgba(#e83d52, 0.08);
  color: darken($lh_color_red, 6%);
}

.um__table {
  background: white;
  border: $um-border;
  border-radius: 16px;
  box-shadow: $lh_shadow_sm;
  // Fixed-track grid; scroll horizontally rather than clip the actions on a
  // narrow content area (e.g. expanded sidebar on a small laptop).
  overflow-x: auto;
  overflow-y: hidden;
}

.um__table-head,
.um__row {
  display: grid;
  grid-template-columns: $um-grid;
  min-width: 800px; // 320+104+128+16+120 tracks + 4×16 gaps + 2×24 padding

  align-items: center;
  column-gap: 16px;
  padding: 0 24px;
}

.um__table-head {
  height: 44px;
  background: $lh_color_snowy_white;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: $lh_color_blue_gray;
}

.um__row {
  min-height: 64px;
  border-top: $um-border;
  transition: background 0.12s;
  &:hover {
    background: #fafbfd;
  }
}

.um__col--user {
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}
.um__col--actions {
  justify-self: end;
  display: flex;
  gap: 8px;
}

.um__avatar {
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 13px;
  font-weight: 700;
  box-shadow: inset 0 0 0 2px rgba(white, 0.15);
}

.um__user-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.um__user-name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.um__user-email {
  font-size: 12px;
  color: $lh_color_blue_gray;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.um__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}
.um__chip--admin {
  background: rgba(#2470ca, 0.1);
  color: $lh_color_blue;
}
.um__chip--member {
  background: $lh_color_snowy_white;
  color: $lh_color_blue_gray;
}
.um__role {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.um__restricted-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(#7a5af8, 0.12);
  color: #7a5af8;
  flex-shrink: 0;
}
.um__chip--active {
  background: rgba(#3d9e4c, 0.12);
  color: darken($lh_color_green, 6%);
}
.um__chip--pending {
  background: rgba(#f08523, 0.12);
  color: darken($lh_color_orange, 8%);
}
.um__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.um__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: $lh_color_blue_gray;
  font-size: 14px;
}

.um__dialog {
  background: white;
  border-radius: 18px;
  padding: 24px;
  box-shadow: $lh_shadow_lg;
}
.um__dialog-title {
  font-size: 18px;
  font-weight: 700;
}
.um__dialog-body {
  margin-top: 10px;
  color: $lh_color_navy_dark;
  font-size: 14px;
}
.um__dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

.um__config-head-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.um__config-sub {
  margin-top: 6px;
}

.um__segment {
  display: inline-flex;
  background: $lh_color_snowy_white;
  border-radius: 10px;
  padding: 3px;
}
.um__segment-btn {
  border: none;
  background: transparent;
  padding: 6px 14px;
  border-radius: 8px;
  font-family: $lh_font_family_dm_sans;
  font-size: 13px;
  font-weight: 600;
  color: $lh_color_blue_gray;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.um__segment-btn--active {
  background: white;
  color: $lh_color_navy_dark;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.um__config-body {
  margin-top: 14px;
  min-height: 120px;
}
.um__an-list {
  display: flex;
  flex-direction: column;
  max-height: 44vh;
  overflow-y: auto;
  scrollbar-gutter: stable;
  border-top: 1px solid rgba(#042143, 0.07);
}
.um__an-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 3px 6px 3px 10px;
  border-bottom: 1px solid rgba(#042143, 0.05);
  cursor: pointer;
  transition: background 0.12s;

  &:hover {
    background: rgba(#eff2f7, 0.6);
  }
  &:last-child {
    border-bottom: none;
  }
}
.um__an-label {
  font-size: 13px;
  color: $lh_color_blue_gray;
  transition: color 0.12s;
}
.um__an-row--on .um__an-label {
  color: $lh_color_navy_dark;
  font-weight: 500;
}
.um__an-switch {
  flex: 0 0 auto;
}
.um__config-count {
  margin-top: 8px;
  font-size: 12px;
  color: $lh_color_blue_gray;
}
.um__lock-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 10px;
  padding: 8px 6px 8px 10px;
  border-top: 1px solid rgba(#042143, 0.07);
  cursor: pointer;
}
.um__lock-text {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.um__lock-title {
  font-size: 13px;
  font-weight: 500;
  color: $lh_color_navy_dark;
}
.um__lock-sub {
  font-size: 11.5px;
  color: $lh_color_blue_gray;
}

.um-fade-enter-active,
.um-fade-leave-active {
  transition: opacity 0.2s;
}
.um-fade-enter-from,
.um-fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
// v-menu content is teleported out of this component, so the scoped block above cannot reach it.
@import "@/styles/_variables.scss";

.um__actions-menu {
  border: 1px solid rgba(#042143, 0.08);
  border-radius: 10px;
  box-shadow: $lh_shadow_lg;

  .um__actions-menu__danger {
    .v-list-item-title,
    .v-icon {
      color: $lh_color_red;
    }
  }
}
</style>
